import React, {
  createContext,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { components as SelectComponents } from 'react-select';
import { ValueContainerProps } from 'react-select/lib/components/containers';
import { useElementRef } from '@tanium/coreui-utils';
import { Box } from '@tanium/react-box';
import { useAggregatedRef } from '@tanium/react-use-aggregated-ref';
import { useResizeObserver } from '@tanium/react-use-resize-observer';

export interface MultiCollapsibleContextType {
  /**
   * The maximum number of pills to display in the collapsible select.
   */
  maxPills?: number;
}

export const MultiCollapsibleContext = createContext<MultiCollapsibleContextType>({});

type Props = ValueContainerProps<unknown>;

export const createValueContainer = (isMultiCollapsible: boolean) => (props: Props) => {
  const {
    selectProps: { menuIsOpen, isDisabled },
    getValue,
    getStyles,
  } = props;

  const values = getValue() as unknown[];
  const pillCountDimensions = useRef(new Map<number, number>());
  const { element, ref: valueContainerRef } = useElementRef<HTMLElement>();
  const [hiddenPills, setHiddenPills] = useState(0);

  const measurePills = useCallback(() => {
    const scrollWidth = element?.scrollWidth;
    const containerWidth = element?.clientWidth;
    if (scrollWidth === undefined || containerWidth === undefined) {
      return;
    }

    // Current selected pills do not fit within the current value container.
    // Try the next suitable number of pills to replace it.
    if (scrollWidth > containerWidth) {
      if (hiddenPills < values.length - 1) {
        pillCountDimensions.current.set(hiddenPills, scrollWidth);
        setHiddenPills(hiddenPills + 1);
      }
      return;
    }

    // Container is much larger than the current rendered number of pills displayed.
    // Try to find a higher number of pills to replace it if available.
    let preferredHiddenPills = 0;
    while (preferredHiddenPills < hiddenPills) {
      const possiblePillsScrollWidth = pillCountDimensions.current.get(preferredHiddenPills);
      if (possiblePillsScrollWidth === undefined || possiblePillsScrollWidth <= containerWidth) {
        break;
      }
      preferredHiddenPills += 1;
    }
    setHiddenPills(preferredHiddenPills);
  }, [element, hiddenPills, values.length]);

  useLayoutEffect(() => {
    pillCountDimensions.current.clear();
    setHiddenPills(0);
  }, [values]);

  useLayoutEffect(() => {
    measurePills();
  }, [measurePills]);

  const shouldCollapsePills = isMultiCollapsible && !menuIsOpen && !isDisabled;

  const containerStyles: React.CSSProperties = useMemo(
    () => ({
      ...getStyles('valueContainer', props),
      ...(shouldCollapsePills && { flexWrap: 'nowrap' }),
    }),
    [getStyles, props, shouldCollapsePills],
  );

  const valueContainerResizeRef = useResizeObserver<HTMLElement>(
    useCallback(() => measurePills(), [measurePills]),
  );

  const containerRefs = useMemo(
    () => [valueContainerRef, valueContainerResizeRef],
    [valueContainerRef, valueContainerResizeRef],
  );
  const aggregatedContainerRefs = useAggregatedRef(containerRefs);

  const contextValue = useMemo<MultiCollapsibleContextType>(
    () => ({ maxPills: shouldCollapsePills ? values.length - hiddenPills : undefined }),
    [hiddenPills, shouldCollapsePills, values.length],
  );

  return isMultiCollapsible ? (
    <MultiCollapsibleContext.Provider value={contextValue}>
      <Box style={containerStyles} ref={aggregatedContainerRefs} overflowX="hidden">
        {props.children}
      </Box>
    </MultiCollapsibleContext.Provider>
  ) : (
    <SelectComponents.ValueContainer {...props} />
  );
};
